import { useEffect, useState } from "react";
import { useShapeLibrary } from "frontend/canvas-designer-new/shape-libraries/hooks/use-shape-library";
import { useLibraryStates } from "../context/library-context";
import styles from "./shapes-library-list.module.css";
import { LibraryRow } from "../types";
import ShapeMenuIcon from "../../shape-menu-icon";
import { PremiumLibraryIcon } from "frontend/icons/premium-library";
import { CursorType } from "frontend/canvas-designer-new/cursor-type";
import { useIsCIMReorderEnabled } from "utils/url-utils";
import cn from "classnames";

export type ShapeInfo = { library: string; group: string; item: string };
export type SelectedItem = { cursorType: CursorType; libraryId: string; groupId: string; itemId: string };

type RowProps = {
  style: React.CSSProperties;
  data: LibraryRow;
  selectedItem: SelectedItem | null;
  onMenuItemClick: (elementType: string, elementData: Record<string, unknown>, elementInfo: ShapeInfo) => void;
  onUpgradeRequired?: () => void;
};

function LibraryHeaderRow({ style, data }: RowProps) {
  const { error } = useShapeLibrary(data.libraryId);
  const { isLibraryExpanded, toggleLibraryExpansion, setLibraryError, setLibraryExpansion } = useLibraryStates();

  useEffect(() => {
    setLibraryError(data.libraryId, error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  useEffect(() => {
    if (data.isPreview) {
      setLibraryExpansion(data.libraryId, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function renderDropdownArrow() {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" width="4" height="8" fill="none" viewBox="0 0 4 8">
        <path stroke="#1E1E1E" strokeLinecap="round" d="m.5.5 2.992 3.07M.5 6.64l2.992-3.07"></path>
      </svg>
    );
  }

  return (
    <div
      style={style}
      className={styles.libraryHeader}
      onClick={() => {
        if (data.isPreview) return;
        toggleLibraryExpansion(data.libraryId);
      }}
    >
      {data.isPreview && <PremiumLibraryIcon size={16} style={{ marginLeft: "8px" }} />}
      <span className={styles.libraryTitle}>{data.text ?? ""}</span>
      <div className={styles.dropdownArrow} data-expanded={isLibraryExpanded(data.libraryId)}>
        {renderDropdownArrow()}
      </div>
    </div>
  );
}

function GroupTitleRow({ style, data }: RowProps) {
  return (
    <div style={style} className={styles.groupTitle}>
      <span>{data.text ?? ""}</span>
    </div>
  );
}

function ItemRow({ style, data, selectedItem, onMenuItemClick, onUpgradeRequired }: RowProps) {
  const {
    data: { thumbnails, mappings },
    isLoading,
    error,
  } = useShapeLibrary(data.libraryId);
  const isCimReorderEnabled = useIsCIMReorderEnabled();
  const iconSize = 26;
  const iconPadding = 8;
  const icons = data.items ?? [];

  if (isLoading || error) {
    return (
      <div style={style} className={cn(styles.itemRow, isCimReorderEnabled && styles.cimItemRow)}>
        {icons.map((_, index) => (
          <div
            key={index}
            className={styles.skeleton}
            style={{ width: iconSize, height: iconSize, margin: iconPadding }}
          />
        ))}
      </div>
    );
  }

  return (
    <div style={style} className={cn(styles.itemRow, isCimReorderEnabled && styles.cimItemRow)}>
      {icons.map((icon, index) => {
        const { elementType, elementData } = mappings[icon.id];
        const iconInfo: ShapeInfo = {
          library: data.libraryId,
          group: data.groupId ?? "",
          item: icon.id,
        };

        const onClick = () => {
          if (data.isPreview) return onUpgradeRequired?.();
          return onMenuItemClick(elementType, elementData, iconInfo);
        };

        const isSelected =
          data.libraryId === selectedItem?.libraryId &&
          data.groupId === selectedItem?.groupId &&
          icon.id === selectedItem?.itemId;

        return (
          <div key={index}>
            <ShapeMenuIcon
              shapeId={icon.id}
              displayName={icon.displayName}
              onMouseDown={onClick}
              thumbnail={thumbnails[icon.id]}
              selected={isSelected}
              size={iconSize}
              padding={iconPadding}
            />
          </div>
        );
      })}
    </div>
  );
}

function ErrorRow({ style, data }: RowProps) {
  const { setLibraryError } = useLibraryStates();
  const { error, mutate } = useShapeLibrary(data.libraryId);
  const [isRetrying, setIsRetrying] = useState(false);

  useEffect(() => {
    setLibraryError(data.libraryId, error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  const handleRetry = () => {
    setIsRetrying(true);
    mutate();
    setTimeout(() => {
      setIsRetrying(false);
    }, 10000);
  };

  return (
    <div style={style} className={styles.errorContainer}>
      <span className={styles.errorText}>Something went wrong when loading this library.</span>
      <button className={styles.reloadButton} onClick={handleRetry} disabled={isRetrying}>
        {isRetrying ? "Retrying..." : "Try again"}
      </button>
    </div>
  );
}

function PreviewFooter({ style, data }: RowProps) {
  return <span style={style} className={styles.previewFooter}>{`+${data.count} shapes`}</span>;
}

function VirtualizedRow({
  index,
  style,
  data,
}: {
  index: number;
  style: React.CSSProperties;
  data: {
    rows: LibraryRow[];
    selectedItem: SelectedItem | null;
    onMenuItemClick: (elementType: string, elementData: Record<string, unknown>, elementInfo: ShapeInfo) => void;
    onUpgradeRequired?: () => void;
  };
}) {
  const row = data.rows[index];
  const rowProps: RowProps = {
    data: row,
    style,
    selectedItem: data.selectedItem,
    onMenuItemClick: data.onMenuItemClick,
    onUpgradeRequired: data.onUpgradeRequired,
  };

  switch (rowProps.data.type) {
    case "library-header": {
      return <LibraryHeaderRow {...rowProps} />;
    }
    case "items-row": {
      return <ItemRow {...rowProps} />;
    }
    case "first-group-title":
    case "group-title": {
      return <GroupTitleRow {...rowProps} />;
    }
    case "error": {
      return <ErrorRow {...rowProps} />;
    }
    case "preview-footer": {
      return <PreviewFooter {...rowProps} />;
    }
    default: {
      return null;
    }
  }
}

export default VirtualizedRow;
